import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";

export default function BreathingGame() {
  const [inhaleSec, setInhaleSec] = useState(4);
  const [holdSec, setHoldSec] = useState(4);
  const [exhaleSec, setExhaleSec] = useState(6);
  const [cycles, setCycles] = useState(5);
  const [running, setRunning] = useState(false);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [timeLeft, setTimeLeft] = useState(0);
  const [playSound, setPlaySound] = useState(true);
  const [selectedMusic, setSelectedMusic] = useState("");
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);

  const controls = useAnimation();
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const oscRef = useRef(null);

  // 🎵 Local music list
  const musicTracks = [
    { label: "None", url: "" },
    { label: "Relaxing Piano 🎹", url: "/music/piano.mp3" },
    { label: "Forest Ambience 🌿", url: "/music/forest_ambience.mp3" },
    { label: "Childhood Memory 🔔", url: "/music/childhood_memory.mp3" },
    { label: "Storm Clouds ☁️", url: "/music/storm-clouds.mp3" },
    { label: "Calm music", url: "/music/Emotional.mp3" },
  ];

  useEffect(() => {
    return () => {
      stopAudio();
      clearInterval(timerRef.current);
      stopMusic();
    };
  }, []);

  // 🔊 Beep sound controls
  function startAudio() {
    if (!playSound) return;
    try {
      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 440;
      gain.gain.value = 0;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      oscRef.current = { osc, gain };
    } catch (e) {
      console.warn("Audio unavailable", e);
    }
  }

  function beep(duration = 150, vol = 0.05) {
    if (!playSound || !audioCtxRef.current || !oscRef.current) return;
    const { gain } = oscRef.current;
    const ctx = audioCtxRef.current;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(vol, now + 0.01);
    gain.gain.linearRampToValueAtTime(0, now + duration / 1000);
  }

  function stopAudio() {
    try {
      if (oscRef.current) {
        const { osc } = oscRef.current;
        osc.stop();
        osc.disconnect();
        oscRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    } catch {}
  }

  // 🎵 Music Controls
  function startMusic() {
    if (selectedMusic && audioRef.current) {
      audioRef.current.play();
      setIsMusicPlaying(true);
    }
  }

  function pauseMusic() {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsMusicPlaying(false);
    }
  }

  function stopMusic() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsMusicPlaying(false);
    }
  }

  // 🌬️ Breathing Session Controls
  function startSession() {
    if (running) return;
    setRunning(true);
    setCurrentCycle(1);
    setPhase("inhale");
    setTimeLeft(inhaleSec);
    startAudio();
    startMusic();
    beep(200, 0.06);
    controls.start({ scale: 1.0 });

    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          nextPhase();
          return 0;
        }
        return Math.max(0, t - 1);
      });
    }, 1000);
  }

  function nextPhase() {
    setPhase((p) => {
      if (p === "inhale") {
        beep(100, 0.04);
        setTimeLeft(holdSec || 0);
        controls.start({ scale: 1.25, transition: { duration: 0.6 } });
        return "hold";
      }
      if (p === "hold") {
        setTimeLeft(exhaleSec || 0);
        beep(150, 0.05);
        controls.start({ scale: 0.75, transition: { duration: exhaleSec } });
        return "exhale";
      }
      if (p === "exhale") {
        setCurrentCycle((c) => {
          const next = c + 1;
          if (next > cycles) {
            finishSession();
            return c;
          }
          setTimeLeft(inhaleSec || 0);
          beep(200, 0.06);
          controls.start({ scale: 1.0, transition: { duration: inhaleSec } });
          return next;
        });
        return "inhale";
      }
      return p;
    });
  }

  function finishSession() {
    clearInterval(timerRef.current);
    timerRef.current = null;
    setRunning(false);
    setPhase("finished");
    stopAudio();
    stopMusic();
    controls.start({ scale: 1.0 });
    beep(120, 0.06);
  }

  function pauseSession() {
    if (!running) return;
    clearInterval(timerRef.current);
    timerRef.current = null;
    setRunning(false);
    stopAudio();
    pauseMusic();
  }

  function resetSession() {
    clearInterval(timerRef.current);
    timerRef.current = null;
    stopAudio();
    stopMusic();
    setRunning(false);
    setPhase("idle");
    setCurrentCycle(0);
    setTimeLeft(0);
    controls.set({ scale: 1 });
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-sky-300 to-indigo-600 text-gray-900">
      <div className="max-w-3xl w-full mx-auto p-6 bg-white/90 backdrop-blur rounded-2xl shadow-xl">
        <h1 className="text-3xl font-bold mb-3 text-center text-indigo-700">
          Breathing Exercise
        </h1>
        <p className="text-sm text-gray-600 mb-4 text-center">
          Follow the rhythm — Inhale → Hold → Exhale
        </p>

        <div className="flex flex-col items-center justify-center">
          <motion.div
            animate={controls}
            initial={{ scale: 0.5 }}
            className="w-64 h-64 rounded-full bg-gradient-to-br from-sky-300 to-indigo-400 shadow-xl"
          />

          <div className="mt-4 text-center">
            <h2 className="text-2xl font-semibold capitalize text-indigo-700">
              {phase}
            </h2>
            <p className="text-gray-600">
              {timeLeft}s • Cycle {currentCycle}/{cycles}
            </p>
          </div>

          {/* Music Selector */}
          <div className="mt-6 w-full max-w-xs text-center">
            <label className="block text-gray-700 font-medium mb-2">
              Background Music
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
              value={selectedMusic}
              onChange={(e) => {
                stopMusic();
                setSelectedMusic(e.target.value);
              }}
            >
              {musicTracks.map((track, i) => (
                <option key={i} value={track.url}>
                  {track.label}
                </option>
              ))}
            </select>

            {selectedMusic && (
              <div className="mt-3 flex justify-center gap-3">
                {!isMusicPlaying ? (
                  <button
                    onClick={startMusic}
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                  >
                    ▶️ Play
                  </button>
                ) : (
                  <button
                    onClick={pauseMusic}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  >
                    ⏸️ Pause
                  </button>
                )}
              </div>
            )}
            <audio ref={audioRef} src={selectedMusic} loop />
          </div>

          {/* Controls */}
          <div className="mt-6 flex gap-3">
            {!running ? (
              <button
                onClick={startSession}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md shadow hover:bg-indigo-700 transition"
              >
                Start
              </button>
            ) : (
              <button
                onClick={pauseSession}
                className="px-6 py-2 bg-yellow-500 text-white rounded-md shadow hover:bg-yellow-600 transition"
              >
                Pause
              </button>
            )}
            <button
              onClick={resetSession}
              className="px-6 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition"
            >
              Reset
            </button>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={playSound}
              onChange={(e) => setPlaySound(e.target.checked)}
            />
            Play Beep Sound
          </label>
        </div>
      </div>
    </div>
  );
}
